import React, { useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { routes, URL_VARS } from '../../constants/routing';
import { DEFAULT_TEMPLATE_CARD_CONFIG } from '../../constants/templates';
import { updateClaimLinkTemplateDetailsRequest } from '../../store/actions/templates';
import { selectIsDeptAdmin, selectUserDepartmentId } from '../../store/selectors/auth';
import { selectCurrentOrganizationDepartmentList } from '../../store/selectors/organizations';
import { ClaimLinkTemplateStatusEnum, IClaimLinkTemplate, ITemplateCardConfig } from '../../types/templates';
import notification from '../../utils/notification';
import { getDepartmentConfirmMessage, hasDepartmentRestrictions } from '../../utils/templates';
import { ConfirmationDialog, RenameClaimLinkTemplateDialog, SplitButton, TemplateCardContextMenu } from '../_mui';
import { ClaimLinkTemplateCardBase } from './ClaimLinkTemplateCardBase';
import { useTemplateCardContextMenu } from './hooks/useTemplateCardContextMenu';

interface IProps {
  template: IClaimLinkTemplate;
  config?: ITemplateCardConfig;
}

export const ClaimLinkTemplateCard: React.FC<IProps> = ({ template, config }) => {
  const history = useHistory();
  const dispatch = useDispatch();
  const isDeptAdmin = useSelector(selectIsDeptAdmin);
  const userDepartmentId = useSelector(selectUserDepartmentId);
  const departmentList = useSelector(selectCurrentOrganizationDepartmentList);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isSetAsDefaultConfirmationOpen, setIsSetAsDefaultConfirmationOpen] = useState(false);

  const { uid, name, status, is_default, cover_image_url, updated_at, department_ids } = template;

  const canDeptAdminEdit = useMemo(() => {
    if (!isDeptAdmin || !userDepartmentId) {
      return true;
    }
    return department_ids?.includes(userDepartmentId) ?? false;
  }, [isDeptAdmin, userDepartmentId, department_ids]);

  const cardConfig = useMemo(() => ({ ...DEFAULT_TEMPLATE_CARD_CONFIG, ...config }), [config]);

  const hasDeptRestrictions = useMemo(() => hasDepartmentRestrictions(department_ids), [department_ids]);

  const { menuAnchorEl, handleMenuOpen, handleMenuClose, handleToggleHiddenClick } =
    useTemplateCardContextMenu(template);

  const handleEditButtonClick = useCallback(() => {
    const url = routes.templates.getTemplateUrl({
      type: URL_VARS.CLAIM_LINK,
      flowId: URL_VARS.EDIT,
      itemId: uid,
    });
    history.push(url);
  }, [history, uid]);

  const handleEditDetailsClick = useCallback(() => {
    setIsEditDialogOpen(true);
    handleMenuClose();
  }, [handleMenuClose]);

  const handleSaveEditDialog = useCallback(
    (templateName: string) => {
      dispatch(
        updateClaimLinkTemplateDetailsRequest({
          uid,
          name: templateName,
          resolve: () => notification.success(undefined, { content: 'Template renamed successfully!' }),
          reject: () => notification.error(undefined, { content: 'Failed to rename template' }),
        }),
      );
      setIsEditDialogOpen(false);
    },
    [dispatch, uid],
  );

  const handleSetAsDefaultClick = useCallback(() => {
    handleMenuClose();
    if (hasDeptRestrictions) {
      setIsSetAsDefaultConfirmationOpen(true);
    } else {
      dispatch(
        updateClaimLinkTemplateDetailsRequest({
          uid,
          is_default: true,
          resolve: () => notification.success(undefined, { content: 'Template set as default!' }),
          reject: () => notification.error(undefined, { content: 'Failed to set template as default' }),
        }),
      );
    }
  }, [dispatch, uid, hasDeptRestrictions, handleMenuClose]);

  const handleConfirmSetAsDefault = useCallback(() => {
    dispatch(
      updateClaimLinkTemplateDetailsRequest({
        uid,
        is_default: true,
        resolve: () => notification.success(undefined, { content: 'Template set as default!' }),
        reject: () => notification.error(undefined, { content: 'Failed to set template as default' }),
      }),
    );
    setIsSetAsDefaultConfirmationOpen(false);
  }, [dispatch, uid]);

  const handleCancelSetAsDefault = useCallback(() => {
    setIsSetAsDefaultConfirmationOpen(false);
  }, []);

  const buttons = useMemo(() => {
    if (cardConfig.actions) {
      return cardConfig.actions(template);
    }

    return (
      <SplitButton
        label="Edit template"
        onClick={handleEditButtonClick}
        onMenuOpen={(event) => handleMenuOpen(event, uid)}
        disabled={!canDeptAdminEdit}
        dataAction="edit_template_btn"
        menuDataAction="edit_template_menu_btn"
      />
    );
  }, [cardConfig, template, canDeptAdminEdit, handleEditButtonClick, handleMenuOpen, uid]);

  const renderContextMenu = useCallback(() => {
    return (
      <TemplateCardContextMenu
        anchorEl={menuAnchorEl}
        isDefault={is_default}
        isActive={status === ClaimLinkTemplateStatusEnum.Live}
        onClose={handleMenuClose}
        onEdit={handleEditDetailsClick}
        onSetAsDefault={handleSetAsDefaultClick}
        onToggleHidden={handleToggleHiddenClick}
      />
    );
  }, [
    menuAnchorEl,
    is_default,
    status,
    handleMenuClose,
    handleEditDetailsClick,
    handleSetAsDefaultClick,
    handleToggleHiddenClick,
  ]);

  const renderEditDialog = useCallback(() => {
    return (
      <RenameClaimLinkTemplateDialog
        open={isEditDialogOpen}
        templateName={name}
        onClose={() => setIsEditDialogOpen(false)}
        onSave={handleSaveEditDialog}
      />
    );
  }, [isEditDialogOpen, name, handleSaveEditDialog]);

  const setAsDefaultConfirmationMessage = useMemo(
    () => getDepartmentConfirmMessage(department_ids ?? [], departmentList ?? []),
    [department_ids, departmentList],
  );

  const renderSetAsDefaultConfirmationDialog = useCallback(() => {
    return (
      <ConfirmationDialog
        open={isSetAsDefaultConfirmationOpen}
        title="Set as organization default?"
        message={setAsDefaultConfirmationMessage}
        confirmButtonText="Set as default"
        onClose={handleCancelSetAsDefault}
        onConfirm={handleConfirmSetAsDefault}
      />
    );
  }, [
    isSetAsDefaultConfirmationOpen,
    setAsDefaultConfirmationMessage,
    handleCancelSetAsDefault,
    handleConfirmSetAsDefault,
  ]);

  const shouldHighlight = useMemo(() => {
    if (cardConfig.isHighlighted) {
      return cardConfig.isHighlighted(template);
    }

    if (cardConfig.highlightDefault) {
      return is_default;
    }

    return false;
  }, [template, cardConfig, is_default]);

  const isCampaignDefault = useMemo(
    () => !!cardConfig.campaignClaimLinkTemplateId && uid === cardConfig.campaignClaimLinkTemplateId,
    [cardConfig.campaignClaimLinkTemplateId, uid],
  );

  const handleCardClick = useMemo(() => {
    if (cardConfig.onCardClick) {
      return () => cardConfig.onCardClick!(template);
    }

    return canDeptAdminEdit ? handleEditButtonClick : undefined;
  }, [cardConfig, template, canDeptAdminEdit, handleEditButtonClick]);

  return (
    <ClaimLinkTemplateCardBase
      thumbnailUrl={cover_image_url}
      title={name}
      status={status}
      actions={buttons}
      isDefault={is_default}
      isCampaignDefault={isCampaignDefault}
      isHighlighted={shouldHighlight}
      updatedAt={updated_at}
      config={cardConfig}
      onClick={handleCardClick}
      fillWidth={cardConfig.fillWidth ?? true}
    >
      {renderContextMenu()}
      {renderEditDialog()}
      {renderSetAsDefaultConfirmationDialog()}
    </ClaimLinkTemplateCardBase>
  );
};
